import { Component, ElementRef, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { DataManagementService } from '../../data-management/data-management.service';
import { HistoricalService } from '../historical.service';
import { VideoService } from '../../realtime/video.service';
import { HeaderService } from 'src/app/header/header.service';
import { SigninService } from 'src/app/signin/signin.service';
import { ToastController } from '@ionic/angular';
import { TranslateService } from '@ngx-translate/core';
import { Group, Vehicule } from 'src/app/model/data-managemnt';
import { DateInterval } from 'src/app/model/date-interval.model';
import { videoUrl } from 'src/app/global.config';
import { MapService } from 'src/app/utils/leaflet/service/map.service';
import { Map } from 'leaflet';
import { DraggableSheetComponent } from 'src/app/shared/components/draggable-sheet/draggable-sheet.component';
import {
  alignSeekToPlaybackChunkStart,
  formatAlertClipFilenameParts,
  formatDateToPlaybackMapToken,
  formatPlaybackSeekToken,
  findPlaybackMapRangeContaining,
  parsePlaybackMapTokenToMillis,
  PlaybackMapRange,
  segmentDurationLabel
} from 'src/app/utils/video-playback/video-playback.utils';
import { forkJoin, of, Subscription } from 'rxjs';
import { catchError } from 'rxjs/operators';
import { RealtimeWorkerService } from 'src/app/pages/realtime/realtime-worker.service';
import * as moment from 'moment';

interface TimelineSegmentLayout {
  range: PlaybackMapRange;
  leftPct: number;
  widthPct: number;
  tooltip: string;
}

interface TimelineTick {
  leftPct: number;
  label: string;
}

interface TimelineEventFlag {
  wallMs: number;
  leftPct: number;
  addedInfo: number;
  ionIcon: string;
  color: string;
  title: string;
  event: any;
}

@Component({
  selector: 'app-video-playback-historical',
  templateUrl: './video-playback-historical.component.html',
  styleUrls: ['./video-playback-historical.component.scss'],
})
export class VideoPlaybackHistoricalComponent implements OnInit, OnDestroy {
  @ViewChild('playback_video') playbackVideoRef: ElementRef<HTMLVideoElement>;
  @ViewChild('draggableSheet') draggableSheet: DraggableSheetComponent;

  loading = false;
  isWakingUp = false;
  wakeupTimeRemaining = 0;
  ranges: PlaybackMapRange[] = [];
  periodEvents: any[] = [];
  videoPlaybackMap: Map;

  windowStartMs: number | null = null;
  windowEndMs: number | null = null;
  private timelineZoomStartMs: number | null = null;
  private timelineZoomEndMs: number | null = null;
  timelineDisplayStart = '';
  timelineDisplayEnd = '';
  timelineSegments: TimelineSegmentLayout[] = [];
  timelineTicks: TimelineTick[] = [];
  timelineEventFlags: TimelineEventFlag[] = [];

  zoomSelecting = false;
  zoomDragStartPct: number | null = null;
  zoomDragCurrentPct: number | null = null;
  private zoomTrackEl: HTMLElement | null = null;
  private readonly boundZoomMove = (e: PointerEvent) => this.onTimelineZoomPointerMove(e);
  private readonly boundZoomUp = (e: PointerEvent) => this.onTimelineZoomPointerUp(e);

  hoverActive = false;
  hoverLeftPct = 0;
  hoverDateLabel = '';

  selectedTimelineEvent: any = null;
  selectedTimelineEventVideoUrl = '';

  /** Inline camera playback under the timeline (fleet-12 frise seek, no map simulation). */
  showPlaybackPlayer = false;
  activeRecordingRange: PlaybackMapRange | null = null;
  selectedCamera: 'driver' | 'front' | 'rear' = 'front';
  trackPlaybackProgress = 0;
  trackPlaybackWallMs: number | null = null;
  private playbackAnchorWallMs: number | null = null;
  private playbackAnchorRealMs: number | null = null;
  private videoPlaybackTickInterval: ReturnType<typeof setInterval> | null = null;
  private lastEmittedPlaybackSecond = -1;

  segmentDurationLabel = segmentDurationLabel;

  private wakeupSubscription: Subscription | null = null;
  private wakeupTimeSubscription: Subscription | null = null;

  constructor(
    public dataManagementService: DataManagementService,
    public historicalService: HistoricalService,
    public signinService: SigninService,
    public videoService: VideoService,
    private headerService: HeaderService,
    private mapService: MapService,
    private toastController: ToastController,
    private translate: TranslateService,
    private realTimeWorker: RealtimeWorkerService,
  ) {}

  ngOnInit(): void {
    this.historicalService.selectedTab = 'video-playback';
    this.dataManagementService.init();
    this.applyWindowFromHistoricalDates();
    this.subscribeToCameraWakeupState();
  }

  ngOnDestroy(): void {
    this.detachTimelineZoomListeners();
    this.stopVideoPlaybackTickLoop();
    this.wakeupSubscription?.unsubscribe();
    this.wakeupTimeSubscription?.unsubscribe();
    this.videoService.teardownPlaybackMapRequests();
    this.videoService.teardownVideoSession(this.dataManagementService.selectedVehicule);
  }

  ionViewDidEnter(): void {
    // Maximize pull-up whenever the video-playback page is opened / re-entered.
    setTimeout(() => this.draggableSheet?.expandMaximized(), 0);

    if (this.mapService.preventMapDestroy) {
      this.mapService.preventMapDestroy = false;
      setTimeout(() => {
        if (this.mapService.map) {
          this.mapService.map.invalidateSize();
          this.realTimeWorker.syncSelectedVehicleMarkerOnMap();
        }
      }, 150);
      return;
    }
    this.mapService.initMap(this.videoPlaybackMap, 'videoPlaybackMap');
    setTimeout(() => {
      if (this.mapService.map) {
        this.mapService.map.invalidateSize();
        this.realTimeWorker.syncSelectedVehicleMarkerOnMap();
      }
    }, 150);
  }

  ionViewWillLeave(): void {
    this.detachTimelineZoomListeners();
    this.stopVideoPlaybackTickLoop();
    if (!this.mapService.preventMapDestroy && this.mapService.map) {
      this.mapService.map.remove();
    }
  }

  get isTimelineZoomed(): boolean {
    return this.timelineZoomStartMs != null && this.timelineZoomEndMs != null;
  }

  get trackSegmentLeftPct(): number {
    if (!this.showPlaybackPlayer || !this.activeRecordingRange) {
      return 0;
    }
    const w0 = this.effectiveTimelineStart();
    const w1 = this.effectiveTimelineEnd();
    const span = Math.max(1, w1 - w0);
    const t0 = this.trackSegmentStartMs;
    if (t0 == null) {
      return 0;
    }
    return Math.max(0, Math.min(100, ((t0 - w0) / span) * 100));
  }

  get trackSegmentWidthPct(): number {
    if (!this.showPlaybackPlayer || !this.activeRecordingRange) {
      return 0;
    }
    const w0 = this.effectiveTimelineStart();
    const w1 = this.effectiveTimelineEnd();
    const span = Math.max(1, w1 - w0);
    const t0 = this.trackSegmentStartMs;
    const t1 = this.trackSegmentEndMs;
    if (t0 == null || t1 == null || t1 <= t0) {
      return 0;
    }
    return Math.max(0, Math.min(100 - this.trackSegmentLeftPct, ((t1 - t0) / span) * 100));
  }

  get trackPlaybackPastLeftPct(): number {
    return this.trackSegmentLeftPct;
  }

  get trackPlaybackPastWidthPct(): number {
    return (this.trackSegmentWidthPct * this.trackPlaybackProgress) / 100;
  }

  get trackPlaybackCursorPct(): number {
    return this.trackSegmentLeftPct + this.trackPlaybackPastWidthPct;
  }

  get trackPlaybackCursorLabel(): string {
    if (this.trackPlaybackWallMs != null) {
      return moment(this.trackPlaybackWallMs).format('DD/MM/YYYY HH:mm:ss');
    }
    const t0 = this.trackSegmentStartMs;
    const t1 = this.trackSegmentEndMs;
    if (t0 == null || t1 == null) {
      return '';
    }
    const wallMs = Math.round(t0 + ((t1 - t0) * this.trackPlaybackProgress) / 100);
    return moment(wallMs).format('DD/MM/YYYY HH:mm:ss');
  }

  private get trackSegmentStartMs(): number | null {
    if (!this.activeRecordingRange?.from) {
      return null;
    }
    const ms = parsePlaybackMapTokenToMillis(this.activeRecordingRange.from);
    return Number.isFinite(ms) ? ms : null;
  }

  private get trackSegmentEndMs(): number | null {
    if (!this.activeRecordingRange?.to) {
      return null;
    }
    const ms = parsePlaybackMapTokenToMillis(this.activeRecordingRange.to);
    return Number.isFinite(ms) ? ms : null;
  }

  get zoomBoxLeftPct(): number {
    if (!this.zoomSelecting || this.zoomDragStartPct == null || this.zoomDragCurrentPct == null) {
      return 0;
    }
    return Math.min(this.zoomDragStartPct, this.zoomDragCurrentPct);
  }

  get zoomBoxWidthPct(): number {
    if (!this.zoomSelecting || this.zoomDragStartPct == null || this.zoomDragCurrentPct == null) {
      return 0;
    }
    return Math.abs(this.zoomDragCurrentPct - this.zoomDragStartPct);
  }

  chooseGroup(group: Group): void {
    this.dataManagementService.selectedGroup = group;
    if (group?.vehicules?.length) {
      const vehicule = group.vehicules[0];
      this.dataManagementService.setSelectedVehicle(vehicule, group);
      this.realTimeWorker.applyHistoriqueVehicleSelection(vehicule, group);
    }
    this.clearResults();
  }

  chooseVehicule(vehicule: Vehicule): void {
    this.dataManagementService.setSelectedVehicle(vehicule, this.dataManagementService.selectedGroup);
    this.realTimeWorker.applyHistoriqueVehicleSelection(vehicule, this.dataManagementService.selectedGroup);
    this.clearResults();
  }

  clearResults(): void {
    this.hidePlaybackPlayer();
    this.ranges = [];
    this.periodEvents = [];
    this.timelineSegments = [];
    this.timelineTicks = [];
    this.timelineEventFlags = [];
    this.clearTimelineEventDetails();
    this.timelineZoomStartMs = null;
    this.timelineZoomEndMs = null;
    this.applyWindowFromHistoricalDates();
  }

  resetTimelineZoom(): void {
    this.timelineZoomStartMs = null;
    this.timelineZoomEndMs = null;
    this.rebuildTimeline();
  }

  async loadRecordingRanges(): Promise<void> {
    const vehicule = this.dataManagementService.selectedVehicule;
    if (!vehicule?.device?.streamId) {
      await this.showToast(this.translate.instant('HISTORICAL.videoPlaybackNoStreamId'), 'warning');
      return;
    }

    if (!this.applyWindowFromHistoricalDates()) {
      await this.showToast(this.translate.instant('HISTORICAL.videoPlaybackPickRange'), 'warning');
      return;
    }

    const startDate = new Date(this.windowStartMs);
    const endDate = new Date(this.windowEndMs);
    const startTok = formatDateToPlaybackMapToken(startDate);
    const endTok = formatDateToPlaybackMapToken(endDate);
    const dateInterval: DateInterval = { startDate, endDate };
    const deviceId = vehicule.device.idDevice;

    this.loading = true;
    this.ranges = [];
    this.periodEvents = [];
    this.clearTimelineEventDetails();
    this.timelineZoomStartMs = null;
    this.timelineZoomEndMs = null;

    try {
      this.videoService.vehicule = vehicule;
      await this.videoService.ensureSignalingForVehicle(vehicule);

      const { mapRes, events } = await forkJoin({
        mapRes: this.videoService.requestPlaybackMap(startTok, endTok),
        events: this.headerService.getEventsByDeviceAndDateRange(deviceId, dateInterval).pipe(
          catchError(() => of([]))
        )
      }).toPromise();

      this.ranges = mapRes?.ranges || [];
      const rawEvents = Array.isArray(events) ? events : events?.data || [];
      this.periodEvents = rawEvents.filter((e: any) => this.isRecognizedEvent(e?.addedInfo));
      this.rebuildTimeline();

      const segCount = this.ranges.length;
      const evCount = this.periodEvents.length;
      if (!segCount && !evCount) {
        await this.showToast(this.translate.instant('HISTORICAL.videoPlaybackNoRanges'), 'info');
      } else if (!segCount && evCount) {
        await this.showToast(
          this.translate.instant('HISTORICAL.videoPlaybackEventsNoRanges', { events: evCount }),
          'info'
        );
      } else {
        await this.showToast(
          this.translate.instant('HISTORICAL.videoPlaybackRangesAndEvents', {
            segments: segCount,
            events: evCount
          }),
          'success'
        );
      }
    } catch (err: any) {
      await this.showToast(
        `${this.translate.instant('HISTORICAL.videoPlaybackMapFailed')}: ${err?.message || err}`,
        'danger'
      );
    } finally {
      this.loading = false;
    }
  }

  onRecordingSegmentClick(seg: TimelineSegmentLayout, ev: Event): void {
    ev.stopPropagation();
    ev.preventDefault();
    this.clearTimelineEventDetails();
    this.openSegmentPlayback(seg.range);
  }

  openSegmentPlayback(range: PlaybackMapRange): void {
    const vehicule = this.dataManagementService.selectedVehicule;
    if (!vehicule?.device?.streamId) {
      void this.showToast(this.translate.instant('VIDEO.error.noVehicle'), 'warning');
      return;
    }
    const startMs = parsePlaybackMapTokenToMillis(range.from);
    const endMs = parsePlaybackMapTokenToMillis(range.to);
    if (!Number.isFinite(startMs) || !Number.isFinite(endMs) || endMs <= startMs) {
      void this.showToast(this.translate.instant('HISTORICAL.videoPlaybackNoRanges'), 'warning');
      return;
    }

    this.clearTimelineEventDetails();
    this.activeRecordingRange = range;
    this.showPlaybackPlayer = true;
    this.trackPlaybackProgress = 0;
    this.trackPlaybackWallMs = startMs;
    this.playbackAnchorWallMs = startMs;
    this.playbackAnchorRealMs = null;
    this.selectedCamera = this.signinService.hasVehicleFrontCameraOption(vehicule) ? 'front' : 'driver';

    this.videoService.vehicule = vehicule;
    this.videoService.type = 'playback';
    this.videoService.playbackMapRanges = this.ranges?.length ? [...this.ranges] : [range];
    this.videoService.recordingRange = range;
    this.videoService.setPlaybackRange(startMs, endMs, range);
    this.videoService.returnRoute = '/video-playback';

    setTimeout(() => {
      this.bindPlaybackVideoElement();
      const channel = this.getPlaybackChannel(this.selectedCamera);
      void this.videoService.startHistoricalPlayback(channel)
        .then(() => this.startVideoPlaybackTickLoop())
        .catch((err) => console.error('Failed to start historical playback', err));
    }, 80);
  }

  hidePlaybackPlayer(): void {
    this.stopVideoPlaybackTickLoop();
    const vehicule = this.dataManagementService.selectedVehicule;
    if (this.videoService.connexion || this.videoService.playback || this.videoService.loadingStream) {
      this.videoService.teardownVideoSession(vehicule);
    }
    this.showPlaybackPlayer = false;
    this.activeRecordingRange = null;
    this.trackPlaybackProgress = 0;
    this.trackPlaybackWallMs = null;
    this.playbackAnchorWallMs = null;
    this.playbackAnchorRealMs = null;
  }

  selectPlaybackCamera(camera: 'driver' | 'front' | 'rear'): void {
    if (!this.showPlaybackPlayer || this.isWakingUp) {
      return;
    }
    if (!this.videoService.connexion) {
      this.videoService.onConnectClick(this.dataManagementService.selectedVehicule);
      return;
    }
    this.selectedCamera = camera;
    this.bindPlaybackVideoElement();
    const channel = this.getPlaybackChannel(camera);
    const wallMs = this.trackPlaybackWallMs ?? this.trackSegmentStartMs;
    if (wallMs == null) {
      return;
    }
    const aligned = this.alignSeekWallMs(wallMs);
    const token = formatPlaybackSeekToken(new Date(aligned));
    this.playbackAnchorWallMs = aligned;
    this.playbackAnchorRealMs = Date.now();
    this.trackPlaybackWallMs = aligned;
    this.trackPlaybackProgress = this.progressFromWallTime(aligned);
    this.lastEmittedPlaybackSecond = -1;
    void this.videoService.Playback(token, channel);
  }

  onTimelinePlaybackSeek(event: Event): void {
    const target = event.target as HTMLInputElement;
    const percentage = parseFloat(target.value);
    if (isNaN(percentage)) {
      return;
    }
    this.applyPlaybackProgress(percentage, false);
  }

  onTimelinePlaybackSeekCommit(event: Event): void {
    const target = event.target as HTMLInputElement;
    const percentage = parseFloat(target.value);
    if (isNaN(percentage)) {
      return;
    }
    this.applyPlaybackProgress(percentage, true);
  }

  private applyPlaybackProgress(percentage: number, commitVideo: boolean): void {
    const pct = Math.max(0, Math.min(100, percentage));
    const t0 = this.trackSegmentStartMs;
    const t1 = this.trackSegmentEndMs;
    if (t0 == null || t1 == null || t1 <= t0) {
      return;
    }

    let wallMs = Math.round(t0 + ((t1 - t0) * pct) / 100);
    if (commitVideo) {
      wallMs = this.alignSeekWallMs(wallMs);
    }
    const alignedPct = this.progressFromWallTime(wallMs);
    this.trackPlaybackProgress = alignedPct;
    this.trackPlaybackWallMs = wallMs;

    if (!commitVideo) {
      this.playbackAnchorWallMs = wallMs;
      this.playbackAnchorRealMs = null;
      return;
    }

    this.sendPlaybackSeekRequest(wallMs, alignedPct);
  }

  private sendPlaybackSeekRequest(wallMs: number, alignedPct: number): void {
    if (!this.videoService.connexion || this.isWakingUp) {
      return;
    }
    if (!this.videoService.playback && !this.videoService.loadingStream) {
      return;
    }
    const channel = this.getPlaybackChannel(this.selectedCamera);
    const token = formatPlaybackSeekToken(new Date(wallMs));
    const infos = `PNormal/${channel}/${channel}REC${token}`;
    this.playbackAnchorWallMs = wallMs;
    this.playbackAnchorRealMs = Date.now();
    this.trackPlaybackProgress = alignedPct;
    this.trackPlaybackWallMs = wallMs;
    this.lastEmittedPlaybackSecond = -1;
    this.videoService.playbackSeekToken = token;
    this.videoService.sendPlaybackRequest(infos);
    this.startVideoPlaybackTickLoop();
  }

  private alignSeekWallMs(seekMs: number): number {
    const match = findPlaybackMapRangeContaining(seekMs, this.ranges)
      || this.activeRecordingRange;
    if (!match) {
      return seekMs;
    }
    const startMs = parsePlaybackMapTokenToMillis(match.from);
    const endMs = parsePlaybackMapTokenToMillis(match.to);
    return alignSeekToPlaybackChunkStart(seekMs, startMs, endMs);
  }

  private progressFromWallTime(wallMs: number): number {
    const t0 = this.trackSegmentStartMs;
    const t1 = this.trackSegmentEndMs;
    if (t0 == null || t1 == null || t1 <= t0) {
      return 0;
    }
    const clamped = Math.max(t0, Math.min(t1, wallMs));
    return ((clamped - t0) / (t1 - t0)) * 100;
  }

  private getPlaybackChannel(camera: 'driver' | 'front' | 'rear'): string {
    const map = { driver: 'CH2', front: 'CH1', rear: 'CH3' };
    return map[camera];
  }

  private bindPlaybackVideoElement(): void {
    if (!this.playbackVideoRef?.nativeElement) {
      return;
    }
    const video = this.playbackVideoRef.nativeElement;
    video.muted = true;
    video.playsInline = true;
    this.videoService.setVideoElement(this.playbackVideoRef);
  }

  private startVideoPlaybackTickLoop(): void {
    this.stopVideoPlaybackTickLoop();
    this.lastEmittedPlaybackSecond = -1;
    this.videoPlaybackTickInterval = setInterval(() => {
      this.syncPlaybackPositionFromClock();
    }, 1000);
  }

  private stopVideoPlaybackTickLoop(): void {
    if (this.videoPlaybackTickInterval) {
      clearInterval(this.videoPlaybackTickInterval);
      this.videoPlaybackTickInterval = null;
    }
    this.lastEmittedPlaybackSecond = -1;
  }

  /**
   * Advance the timeline playhead from wall-clock while WebRTC plays.
   * MediaStream `video.currentTime` is unreliable on mobile WebViews.
   */
  private syncPlaybackPositionFromClock(): void {
    if (!this.showPlaybackPlayer || this.playbackAnchorWallMs == null) {
      return;
    }
    if (!this.videoService.playback && !this.videoService.loadingStream) {
      return;
    }
    if (this.playbackAnchorRealMs == null) {
      if (this.videoService.playback) {
        this.playbackAnchorRealMs = Date.now();
      }
      return;
    }
    const elapsedSeconds = Math.floor((Date.now() - this.playbackAnchorRealMs) / 1000);
    if (elapsedSeconds === this.lastEmittedPlaybackSecond) {
      return;
    }
    this.lastEmittedPlaybackSecond = elapsedSeconds;
    const t1 = this.trackSegmentEndMs;
    let wallMs = this.playbackAnchorWallMs + elapsedSeconds * 1000;
    if (t1 != null && wallMs > t1) {
      wallMs = t1;
    }
    this.trackPlaybackWallMs = wallMs;
    this.trackPlaybackProgress = this.progressFromWallTime(wallMs);
  }

  onTimelineEventFlagClick(ef: TimelineEventFlag, ev: Event): void {
    ev.stopPropagation();
    ev.preventDefault();
    this.selectedTimelineEvent = ef.event;
    this.selectedTimelineEventVideoUrl = this.constructAlertVideoUrl(ef.event);
  }

  clearTimelineEventDetails(): void {
    this.selectedTimelineEvent = null;
    this.selectedTimelineEventVideoUrl = '';
  }

  getEventTypeKey(type: number): string {
    const eventTypeMap: { [key: number]: string } = {
      0: 'ALGO_WARN_TYPE_NONE',
      257: 'ALGO_WARN_TYPE_FCW',
      258: 'ALGO_WARN_TYPE_FPW',
      259: 'ALGO_WARN_TYPE_LDW',
      260: 'ALGO_WARN_TYPE_VIRTUAL_BUMPERS',
      265: 'ALGO_WARN_TYPE_CALLPHONE',
      266: 'ALGO_WARN_TYPE_SMOKING',
      267: 'ALGO_WARN_TYPE_DISTRACTION',
      268: 'ALGO_WARN_TYPE_TIRED',
      270: 'ALGO_WARN_TYPE_SEATBELT',
      276: 'ALGO_WARN_TYPE_EMERGENCY',
      233: 'ALGO_WARN_HIGH_RPM',
      234: 'ALGO_WARN_SPEEDING',
      235: 'ALGO_WARN_BRAKE',
      236: 'ALGO_WARN_ROT'
    };
    return eventTypeMap[type] || 'ALGO_WARN_TYPE_NONE';
  }

  getEventIonIcon(type: number): string {
    const iconMap: { [key: number]: string } = {
      257: 'car-outline',
      258: 'walk-outline',
      259: 'navigate-outline',
      260: 'shield-outline',
      265: 'call-outline',
      266: 'flame-outline',
      267: 'eye-off-outline',
      268: 'bed-outline',
      270: 'person-remove-outline',
      276: 'warning-outline',
      233: 'speedometer-outline',
      234: 'arrow-forward-outline',
      235: 'arrow-back-outline',
      236: 'refresh-outline',
      0: 'information-circle-outline'
    };
    return iconMap[type] || 'alert-circle-outline';
  }

  getEventColorStyle(addedInfo: number): string {
    const palette = [
      '#7cb5ec', '#434348', '#90ed7d', '#f7a35c', '#8085e9', '#f15c80', '#e4d354', '#2b908f', '#f45b5b',
      '#91e8e1', '#4498a1', '#f7a35c', '#8085e9', '#f15c80'
    ];
    const colorIndexMap: { [key: number]: number } = {
      257: 0, 258: 1, 259: 2, 260: 3, 265: 4, 266: 5, 267: 6, 268: 7,
      270: 8, 276: 9, 233: 10, 234: 11, 235: 12, 236: 13
    };
    const idx = colorIndexMap[addedInfo];
    return idx !== undefined ? palette[idx] : '#777';
  }

  getEventDescription(event: any): string {
    if (!event) {
      return '';
    }
    const key = this.getEventTypeKey(event.addedInfo);
    const desc = this.translate.instant('EVENTS.DESCRIPTIONS.' + key);
    if (desc && typeof desc === 'string' && !desc.startsWith('EVENTS.')) {
      return desc;
    }
    return this.translate.instant('EVENTS.DESCRIPTIONS.ALGO_WARN_TYPE_NONE');
  }

  onTimelinePointerMove(ev: PointerEvent): void {
    if (this.windowStartMs == null || this.windowEndMs == null || this.zoomSelecting) {
      return;
    }
    if (this.showPlaybackPlayer) {
      return;
    }
    // Hover date on coarse pointers (touch) is noisy; keep for mouse/pen.
    if (ev.pointerType === 'touch') {
      return;
    }
    const trackEl = ev.currentTarget as HTMLElement;
    const ratio = this.ratioFromClientX(trackEl, ev.clientX);
    this.hoverLeftPct = ratio * 100;
    const w0 = this.effectiveTimelineStart();
    const w1 = this.effectiveTimelineEnd();
    const wallMs = Math.round(w0 + ratio * (w1 - w0));
    this.hoverDateLabel = moment(wallMs).format('DD/MM/YYYY HH:mm:ss');
    this.hoverActive = true;
  }

  onTimelinePointerLeave(): void {
    this.clearTimelineHover();
  }

  timelineZoomPointerDown(ev: PointerEvent): void {
    const el = ev.target as HTMLElement;
    if (el.closest('.vp-timeline-overlay') || el.closest('.vp-timeline-event-flag')) {
      return;
    }
    if (el.closest('.vp-timeline-playback-seek-wrap')) {
      return;
    }
    // Zoom disabled while camera playback is open (fleet-12).
    if (this.showPlaybackPlayer) {
      return;
    }
    if (ev.button != null && ev.button !== 0) {
      return;
    }
    if (this.windowStartMs == null || this.windowEndMs == null) {
      return;
    }
    ev.preventDefault();
    this.clearTimelineHover();
    this.detachTimelineZoomListeners();
    this.zoomTrackEl = ev.currentTarget as HTMLElement;
    this.zoomSelecting = true;
    const ratio = this.ratioFromClientX(this.zoomTrackEl, ev.clientX);
    const pct = ratio * 100;
    this.zoomDragStartPct = pct;
    this.zoomDragCurrentPct = pct;
    try {
      this.zoomTrackEl.setPointerCapture(ev.pointerId);
    } catch {
      // ignore
    }
    document.addEventListener('pointermove', this.boundZoomMove);
    document.addEventListener('pointerup', this.boundZoomUp);
    document.addEventListener('pointercancel', this.boundZoomUp);
  }

  private onTimelineZoomPointerMove(ev: PointerEvent): void {
    if (!this.zoomSelecting || !this.zoomTrackEl) {
      return;
    }
    const ratio = this.ratioFromClientX(this.zoomTrackEl, ev.clientX);
    this.zoomDragCurrentPct = ratio * 100;
  }

  private onTimelineZoomPointerUp(_ev: PointerEvent): void {
    document.removeEventListener('pointermove', this.boundZoomMove);
    document.removeEventListener('pointerup', this.boundZoomUp);
    document.removeEventListener('pointercancel', this.boundZoomUp);
    this.zoomTrackEl = null;
    if (!this.zoomSelecting || this.windowStartMs == null || this.windowEndMs == null) {
      this.zoomSelecting = false;
      this.zoomDragStartPct = null;
      this.zoomDragCurrentPct = null;
      return;
    }
    this.zoomSelecting = false;
    const startPct = this.zoomDragStartPct;
    const endPct = this.zoomDragCurrentPct;
    this.zoomDragStartPct = null;
    this.zoomDragCurrentPct = null;
    if (startPct == null || endPct == null) {
      return;
    }
    const dragWidthPct = Math.abs(endPct - startPct);
    if (dragWidthPct < 1.5) {
      return;
    }
    const v0 = this.effectiveTimelineStart();
    const v1 = this.effectiveTimelineEnd();
    const span = Math.max(1, v1 - v0);
    const loPct = Math.max(0, Math.min(100, Math.min(startPct, endPct)));
    const hiPct = Math.max(0, Math.min(100, Math.max(startPct, endPct)));
    let tA = Math.round(v0 + (loPct / 100) * span);
    let tB = Math.round(v0 + (hiPct / 100) * span);
    if (tB <= tA) {
      return;
    }
    const minSpanMs = 30_000;
    if (tB - tA < minSpanMs) {
      const mid = (tA + tB) / 2;
      tA = Math.round(mid - minSpanMs / 2);
      tB = Math.round(mid + minSpanMs / 2);
    }
    const cal0 = this.windowStartMs;
    const cal1 = this.windowEndMs;
    this.timelineZoomStartMs = Math.max(cal0, Math.min(cal1, tA));
    this.timelineZoomEndMs = Math.max(cal0, Math.min(cal1, tB));
    if (this.timelineZoomEndMs <= this.timelineZoomStartMs) {
      this.timelineZoomStartMs = null;
      this.timelineZoomEndMs = null;
      return;
    }
    this.rebuildTimeline();
  }

  private rebuildTimeline(): void {
    this.timelineSegments = [];
    this.timelineTicks = [];
    if (this.windowStartMs == null || this.windowEndMs == null) {
      this.timelineEventFlags = [];
      return;
    }
    this.syncTimelineAxisLabelsFromView();
    const w0 = this.effectiveTimelineStart();
    const w1 = this.effectiveTimelineEnd();
    const span = Math.max(1, w1 - w0);
    const minBarPct = 0.35;

    for (const r of this.ranges) {
      const a = parsePlaybackMapTokenToMillis(r.from);
      const b = parsePlaybackMapTokenToMillis(r.to);
      const clipA = Math.max(w0, Math.min(w1, a));
      const clipB = Math.max(w0, Math.min(w1, b));
      if (clipB <= clipA) {
        continue;
      }
      const leftPct = ((clipA - w0) / span) * 100;
      let widthPct = ((clipB - clipA) / span) * 100;
      if (widthPct < minBarPct) {
        widthPct = minBarPct;
      }
      this.timelineSegments.push({
        range: r,
        leftPct,
        widthPct,
        tooltip: `${r.from} → ${r.to} (${this.segmentDurationLabel(r)})`
      });
    }

    const divisions = 6;
    const sameDay = moment(w0).isSame(moment(w1), 'day');
    for (let i = 0; i <= divisions; i++) {
      const t = w0 + (span * i) / divisions;
      const leftPct = (i / divisions) * 100;
      const label = sameDay ? moment(t).format('HH:mm') : moment(t).format('DD/MM HH:mm');
      this.timelineTicks.push({ leftPct, label });
    }

    this.rebuildEventFlagsInWindow();
  }

  private rebuildEventFlagsInWindow(): void {
    this.timelineEventFlags = [];
    if (this.windowStartMs == null || this.windowEndMs == null || !this.periodEvents?.length) {
      return;
    }
    const w0 = this.effectiveTimelineStart();
    const w1 = this.effectiveTimelineEnd();
    const span = Math.max(1, w1 - w0);
    for (const ev of this.periodEvents) {
      if (!ev?.date) {
        continue;
      }
      const wallMs = moment(ev.date).valueOf();
      if (wallMs < w0 || wallMs > w1) {
        continue;
      }
      if (!this.isRecognizedEvent(ev.addedInfo)) {
        continue;
      }
      const leftPct = ((wallMs - w0) / span) * 100;
      this.timelineEventFlags.push({
        wallMs,
        leftPct,
        addedInfo: ev.addedInfo,
        ionIcon: this.getEventIonIcon(ev.addedInfo),
        color: this.getEventColorStyle(ev.addedInfo),
        title: this.buildEventFlagTitle(ev),
        event: ev
      });
    }
  }

  private buildEventFlagTitle(ev: any): string {
    const t = moment(ev.date).format('DD/MM/YYYY HH:mm:ss');
    const key = this.getEventTypeKey(ev.addedInfo);
    const desc = this.translate.instant('EVENTS.DESCRIPTIONS.' + key);
    if (desc && typeof desc === 'string' && !desc.startsWith('EVENTS.')) {
      return `${t} — ${desc}`;
    }
    return t;
  }

  private applyWindowFromHistoricalDates(): boolean {
    const start = this.historicalService.startDate;
    const end = this.historicalService.endDate;
    if (!start || !end) {
      this.windowStartMs = null;
      this.windowEndMs = null;
      return false;
    }
    let w0 = moment(start).valueOf();
    let w1 = moment(end).valueOf();
    if (w1 < w0) {
      const tmp = w0;
      w0 = w1;
      w1 = tmp;
    }
    this.windowStartMs = w0;
    this.windowEndMs = w1;
    this.timelineZoomStartMs = null;
    this.timelineZoomEndMs = null;
    this.syncTimelineAxisLabelsFromView();
    this.rebuildTimeline();
    return true;
  }

  private syncTimelineAxisLabelsFromView(): void {
    const w0 = this.effectiveTimelineStart();
    const w1 = this.effectiveTimelineEnd();
    this.timelineDisplayStart = moment(w0).format('DD/MM/YYYY HH:mm');
    this.timelineDisplayEnd = moment(w1).format('DD/MM/YYYY HH:mm');
  }

  private effectiveTimelineStart(): number {
    if (this.windowStartMs == null) {
      return 0;
    }
    return this.timelineZoomStartMs ?? this.windowStartMs;
  }

  private effectiveTimelineEnd(): number {
    if (this.windowEndMs == null) {
      return 0;
    }
    return this.timelineZoomEndMs ?? this.windowEndMs;
  }

  private ratioFromClientX(trackEl: HTMLElement, clientX: number): number {
    const rect = trackEl.getBoundingClientRect();
    return Math.max(0, Math.min(1, rect.width > 0 ? (clientX - rect.left) / rect.width : 0));
  }

  private clearTimelineHover(): void {
    this.hoverActive = false;
    this.hoverLeftPct = 0;
    this.hoverDateLabel = '';
  }

  private detachTimelineZoomListeners(): void {
    document.removeEventListener('pointermove', this.boundZoomMove);
    document.removeEventListener('pointerup', this.boundZoomUp);
    document.removeEventListener('pointercancel', this.boundZoomUp);
    this.zoomTrackEl = null;
    if (this.zoomSelecting) {
      this.zoomSelecting = false;
      this.zoomDragStartPct = null;
      this.zoomDragCurrentPct = null;
    }
  }

  private subscribeToCameraWakeupState(): void {
    this.wakeupSubscription = this.videoService.isWakingUp$.subscribe((waking) => {
      this.isWakingUp = waking;
    });
    this.wakeupTimeSubscription = this.videoService.wakeupTimeRemaining$.subscribe((remaining) => {
      this.wakeupTimeRemaining = remaining;
    });
  }

  private isRecognizedEvent(type: number): boolean {
    if (type == null || isNaN(+type)) {
      return false;
    }
    const recognizedTypes = [0, 257, 258, 259, 260, 265, 266, 267, 268, 270, 276, 233, 234, 235, 236];
    return recognizedTypes.includes(+type);
  }

  private constructAlertVideoUrl(event: any): string {
    const vehicule = this.dataManagementService.selectedVehicule;
    if (!vehicule?.device?.streamId || !event?.date) {
      return '';
    }
    const eventInstant = new Date(event.date);
    if (isNaN(eventInstant.getTime())) {
      return '';
    }
    let alertId = event.addedInfo;
    if (event.addedInfo >= 256 && event.addedInfo <= 260) {
      alertId = 4;
    } else if (event.addedInfo >= 265 && event.addedInfo <= 270) {
      alertId = 5;
    }
    const { dateStr, timeStr } = formatAlertClipFilenameParts(eventInstant);
    return `${videoUrl}${vehicule.device.streamId}/alert/alert-${alertId}-${dateStr}-${timeStr}.mp4`;
  }

  private async showToast(message: string, color: string): Promise<void> {
    const toast = await this.toastController.create({ message, duration: 3500, color, position: 'bottom' });
    await toast.present();
  }
}
